import React, {useState, useEffect, useRef, useContext, memo, useMemo} from 'react';
import {useNavigate} from 'react-router-dom';
import {Container, Box, Grid} from '@mui/material';
import { ChatMessages } from "./ChatMessages.js";
import {getKBKey, setKBKey} from "../App/cryptoUtils.js";
import {getQueryParamValue, isMobile, parseJSON, publicChatData, publicChatToken} from "../App/utils.js";
import {decryptKBFields, getKB, updateChatModel, updateVariable} from "../API/KB/KBAPI.js";
import {AlertContext, APIContext, drawerWidth} from "../App/App.js";
import Loader from "../Loaders/LocalLoader.js";
import {
    autoscrollBlocked,
    blockAutoscroll,
    convertContentWithImageObjectToArrayOfObjects, generateMsgId,
    getContentRenderURL,
    loadChatHistory,
    saveChatHistory
} from "./utils.js";
import {ChatAvatarBackground} from "./ChatAvatarBackground.js";
import {ChatInput} from "./ChatInput.js";
import {createOnImageToTextResponse} from "../InputTools/ImageToText.js";
import {ChatInputProvider} from "../Context/ChatInputProvider";
import {ChatButtons} from "./ChatButtons";
import {createHandleWebsocketMessages, createRequestChatAPI, onChangeChat} from "./ChatActions";
import {EnableNotificationsButton} from "../PWA/EnableNotificationsButton";
import {kbUserData, kbUserDataLoaded} from "../utils";
import {hasRecentMessageAction} from "./ChatMessage";
import {WS_MESSAGE} from "../App/constants";
import {initDB} from "../Engines/Dexie";
import axios from "axios";
import DynamicComponentLoader from "./DynamicComponentLoader";
import * as itemsAPI from "../API/items/itemsAPI";
import * as indexedDB from "../Engines/Dexie";
import * as chatAPI from "../API/chatAPI";
import {renderChatModelsSelect, renderImageGenerationModelsSelect} from "./ChatSettings";
import {ChatModels, MyServices, VisualModels} from "../constants";
import {ON_PREMISES_CHAT_MODELS, updateChat} from "../API/chatAPI";

let lastWarmup = -Infinity;

export const isKBImageEditor = (kbData) => {
    return kbData?.inputTools?.some?.(tool => parseJSON(tool)?.type === 'imageEditor');
}

let chatInteractions = 0;
let chatInitialLoad = false
let lastContent = '';

export const getGroupedImageGenerationModels = () => {
    const groups = Object.entries(VisualModels)
        .filter(([_, model]) => model.modelType === 'generation')
        .reduce((acc, [key, model]) => {
            const groupKey = model.group;
            acc[groupKey] = acc[groupKey] || [];
            acc[groupKey].push({ id: key, ...model });
            return acc;
        }, {});

    return Object.fromEntries(
        Object.entries(groups).sort(([a], [b]) => a.localeCompare(b))
    );
}

export const  getGroupedModels = () => {
    const groups = {};
    const MODELS = ON_PREMISES_CHAT_MODELS || ChatModels

    Object.keys(MODELS).forEach(key => {
        const model = MODELS[key];
        const groupKey = model.obsolete ? 'Obsolete' : model.size;
        groups[groupKey] = groups[groupKey] || [];
        groups[groupKey].push({ id: key, ...model });
    });

    return Object.keys(groups)
        .sort((a, b) => (a === 'Obsolete' ? 1 : b === 'Obsolete' ? -1 : a.localeCompare(b)))
        .reduce((sortedGroups, key) => {
            sortedGroups[key] = groups[key];
            return sortedGroups;
        }, {});
}

export const Chat = memo(({open, kbIdAsSubdomain, setUnreadMessages, KBData, setKBData, chatId, navigateToChat, kbChats,
    setKBChats, isStreaming, setIsStreaming, setKBKeyIsSet, newKB, renderSettings, setRenderSettings,
    dynamicRenderLoaded, contentRenderLoaded, notificationsPermissionStatus, setNotificationsPermissionStatus,
    ChatData, setChatData
}) => {
    const groupedModels = useMemo(getGroupedModels, []);
    const groupedImageGenerationModels = useMemo(getGroupedImageGenerationModels, []);
    let kbId = kbIdAsSubdomain;
    const navigate = useNavigate();
    const abortController = useRef(null);

    const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null);
    const [messages, setMessages] = useState([]);
    const [typing, setTyping] = useState({});
    const [wideScroll, setWideScroll] = useState(false);
    const [KB, setKB] = useState({});
    const [KBEncrypted, setKBEncrypted] = useState({});
    const [blockingLoading, setBlockingLoading] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [itemsAreLoading, setItemsAreLoading] = useState(false);
    const [imageFiles, setImageFile] = useState({});
    const chatContainerRef = useRef(null);
    const dynamicContainerRef = useRef(null);
    const sendButtonRef = useRef(null);
    const sendButtonRippleRef = useRef(null);
    const scrollUpCount = useRef(0);
    const lastScrollTop = useRef(0);

    const {setSystemAlert} = useContext(AlertContext);

    // speechToText
    const [isRecording, setIsRecording] = useState(false);
    const isRecordingRef = useRef(isRecording);
    const [isTalking, setIsTalking] = useState(false);
    const [micEnabled, setMicEnabled] = useState(true);
    const [micLoading, setMicLoading] = useState(false);
    const {APIData, setAPIData} = useContext(APIContext);
    const chatAbortController = useRef(null);
    const [isImageEditor, setIsImageEditor] = useState(false);

    const [hasIndexedDBItems, setHasIndexedDBItems] = useState(null);
    const [contextItems, setContextItems] = useState([]);
    const [isContextItemsOpen, setIsContextItemsOpen] = useState(false);
    const [totalTokensSum, setTotalTokensSum] = useState(0);
    const [isPWA, setIsPWA] = useState(false);

    const [contextWindow, setContextWindow] = useState(() => {
        const storedValue = localStorage.getItem('contextWindow');
        return storedValue !== null ? JSON.parse(storedValue) : null;
    });

    useEffect(() => {
        localStorage.setItem('contextWindow', JSON.stringify(contextWindow));
    }, [contextWindow]);

    const [addressToAI, setAddressToAI] = useState(() => {
        const storedValue = localStorage.getItem('addressToAI');
        return storedValue !== null ? JSON.parse(storedValue) : true;
    });

     // await db.hasItems

    useEffect(() => {
        localStorage.setItem('addressToAI', JSON.stringify(addressToAI));
    }, [addressToAI]);


    useEffect(() => {
        const handleWebsocketMessages = createHandleWebsocketMessages({chatId, setMessages, setTyping, setUnreadMessages, chatContainerRef});
        window.addEventListener('onWebsocketMessage', handleWebsocketMessages);
        return () => {
            window.removeEventListener('onWebsocketMessage', handleWebsocketMessages);
        };
    }, [chatId]);

    useEffect(() => {
        if (isKBImageEditor(KBData)) setIsImageEditor(true);
    }, [KBData]);

    const fetchKB = (kbId) => {
        getKB(false).then((data) => {
            if (!data) return;

            setKBEncrypted(data);
            const decryptedKBData = decryptKBFields(data, kbId);
            setKB(decryptedKBData);
            setAPIData((prev) => ({...prev, KBData: decryptedKBData}));
        })
    }

    useEffect(() => {
        onChangeChat({ chatId, setUnreadMessages, messages, setMessages, chatAbortController, setLoading, isImageEditor, setLastEvaluatedKey, lastEvaluatedKey })
        return () => chatAbortController.current?.abort();
    }, [chatId]);

    useEffect(() => {
        isRecordingRef.current = isRecording;
        if (isRecording) setBlockingLoading(false);
    }, [isRecording]);

    useEffect(() => {
        if (!kbId) return;
        fetchKB(kbId);
    }, [kbId]);

    useEffect(() => {
        if(KBData?.walletPrivateKey) {

            if (KBData?.searchEngine === 'IndexedDB' && KBData?.kbId) {
                initDB({KBData, setSystemAlert, setLoading}).then((db) => {
                    db.hasItems().then(res => {
                        setHasIndexedDBItems(res)
                    })

                    // fetch items from search engine
                });
            }
        }
    }, [KBData]);

    // #imageEditorChat
    useEffect(() => {
        if (!kbId) return;
        if (isImageEditor) loadChatHistory(kbId, setMessages);
    }, [isImageEditor]);

    // scroll down once if any messages
    useEffect(() => {
        if (messages?.length && !chatInitialLoad && !isImageEditor) {
            const timer = setTimeout(() => {
                chatInitialLoad = true;
                chatContainerRef?.current?.scrollTo(0, chatContainerRef.current.scrollHeight + 1000);
            }, 1000);

            return () => clearTimeout(timer);
        }
    }, [messages]);

    useEffect(  () => {
        if (publicChatToken) return;
        if (!kbId) return;
        const fetchKey = async () => {
            if (!getKBKey(kbId)) {
                setKBKey(kbId, (await getKB()).key);
                fetchKB(kbId);
            }
            setKBKeyIsSet(true)
        };

        fetchKey();
    }, []);

    useEffect(  () => {
        if (KBData?.accountId && lastWarmup < +new Date() - 60000) {
            // console.log('warmup stream')
            // warmupChat(KBData)
            lastWarmup = +new Date();
        }
    }, [KBData]);

    const lastMessageRef = useRef(null);

    useEffect(() => {
        const lastMsg = messages?.[messages?.length - 1];
        if (!lastMsg) return;

        const isLastMe = lastMsg?.userId === 'User_' + kbUserDataLoaded?.myUserId
        const delay = isLastMe ? 1 : 50;

        const isNewMessage = lastMessageRef.current !== lastMsg;
        const isContentChanged = lastContent !== lastMsg.content;
        const someoneIsTyping = typing?.userId && !isLastMe
        const someoneWasTyping = lastMessageRef.current?.type === WS_MESSAGE.USER_TYPING

        if (isNewMessage || isContentChanged || someoneIsTyping) {
            if (someoneIsTyping) {
                lastMessageRef.current = typing;
            } else {
                lastMessageRef.current = lastMsg;
            }
        } else {
            lastMessageRef.current = lastMsg;
            return;
        }

        const handleScroll = () => {
            if (autoscrollBlocked() || renderSettings?.disableAutoscroll) return;
            if (messages.length && !isImageEditor && !hasRecentMessageAction()) {
                chatContainerRef?.current?.scrollTo(0, chatContainerRef.current.scrollHeight + 1000);
            }
        }

        setTimeout(() => handleScroll(), delay)

        // #imageEditorChat
        if (isImageEditor) saveChatHistory(kbId, messages);

        chatInteractions++;
    }, [messages, isImageEditor, typing, chatContainerRef]);


    useEffect(() => {
        const chatContainer = chatContainerRef.current;

        const handleScroll = () => {
            const currentScrollTop = chatContainer.scrollTop;

            if (currentScrollTop < lastScrollTop.current) {
                scrollUpCount.current++;
                if (scrollUpCount.current >= 2) {
                    blockAutoscroll();
                }
            } else {
                scrollUpCount.current = 0;
            }

            lastScrollTop.current = currentScrollTop;

            if (!chatContainer.hasScrolled) {
                chatContainer.hasScrolled = 1;
            } else {
                chatContainer.hasScrolled++;
            }
        }

        chatContainer.addEventListener('scroll', handleScroll);

        return () => {
            chatContainer.removeEventListener('scroll', handleScroll);
        };
    }, [blockAutoscroll])

    const itemsAsSearchResults = [];

    // const itemsAsSearchResults = items?.map(o => ({
    //     _source: {
    //         kbId: o.kbId, body: o.body,
    //         itemId: o.itemId, userId: o.userId, totalTokens: o.totalTokens
    //     }
    // }));

    const RequestChatAPI = createRequestChatAPI({ KBData,
        setIsStreaming, setMessages, setSystemAlert, abortController, isImageEditor, kbId, KB, navigateToChat,
        setKBChats, itemsAsSearchResults, imageFiles, setBlockingLoading, setImageFile,
        contextWindow, setTotalTokensSum, setContextItems, renderSettings
    });

    const [chatContainerHeight, setChatContainerHeight] = useState(600);
    const [chatContainerWidth, setChatContainerWidth] = useState('100%');

    // ImageToText
    const onImageToTextResponse = createOnImageToTextResponse({
        kbId,
        setBlockingLoading,
        imageFiles,
        setImageFile,
        messages: [],
        RequestChatAPI
    });

    // Start new chat from query
    useEffect(() => {
        const startNewChat = getQueryParamValue('startNewChat');
        if (startNewChat && KBData?.accountId) {
            const newMessage = {
                role: 'user',
                content: startNewChat,
                userId: kbUserData().chatUsername,
                msgId: generateMsgId()
            };
            RequestChatAPI([newMessage]).then();
        }
    }, [KBData]);



    // #resize 2
    useEffect(() => {
        const updateChatContainerSize = () => {
            const rect = chatContainerRef.current.getBoundingClientRect();
            const offset = isMobile ? 120 : 70;
            const newHeight = window.innerHeight - rect.top - offset;
            const newWidth = window.innerWidth - rect.left - offset;

            if (renderSettings?.chatContainerHeight) {
                setChatContainerHeight(renderSettings?.chatContainerHeight);
            } else {
                setChatContainerHeight(newHeight);
            }

            setChatContainerWidth((isMobile ? null : newWidth));
        };

        updateChatContainerSize();
        window.addEventListener('resize', updateChatContainerSize);

        return () => window.removeEventListener('resize', updateChatContainerSize);
    }, [dynamicRenderLoaded]);

    let marginLeft = -40
    if (isImageEditor) marginLeft = isMobile ? -65 : -200;

    const chatContainerStyle = {
        height: chatContainerHeight + (publicChatData ? 55 : 0),
        width: chatContainerWidth,
        marginTop: publicChatData ? -15 : (isMobile ? -5 : 10),
        // marginTop: 10,
        marginLeft,
        marginRight: isMobile ? -56 : -40,
    };

    const loadedComponentParams = {
        messages, setMessages, KB, chatContainerRef, RequestChatAPI, setSystemAlert, axios,
        itemsAPI, renderSettings, setRenderSettings, indexedDB, chatAPI,
        setBlockingLoading, blockingLoading
    };

    const myKB = publicChatData
        ? {
            kbId: publicChatData?.publicKBId,
            kbTitle: 'Chat'
        }
        : KB;

    const inputComponentParams = {
        isStreaming,setIsRecording,isRecordingRef,setIsTalking,onImageToTextResponse,
        setBlockingLoading,KB,setMicEnabled,micEnabled,isRecording,isTalking,
        setMicLoading,micLoading,messages,addressToAI, setAddressToAI, setMessages,
        RequestChatAPI, navigateToChat, setKBChats, itemsAreLoading, chatId,
        isContextItemsOpen, setIsContextItemsOpen, renderSettings, ChatData,
        sendButtonRippleRef
    };

    const chatButtonsParams = { isImageEditor, kbIdAsSubdomain, navigateToChat, isStreaming,
        setIsStreaming, RequestChatAPI, setKBChats, itemsAreLoading, chatId,
        addressToAI, messages, setMessages, abortController, ChatButtons,
        sendButtonRippleRef
    }

    return (
        <Container style={{width: '100%'}} >
            <Loader open={(isLoading)} blocking={false} />
            <Loader open={!!blockingLoading} blocking={true} text={blockingLoading?.text} icon={blockingLoading?.icon} />
            {!renderSettings?.disablePushNotificationsButton && !isImageEditor && (!isMobile || isPWA) && <EnableNotificationsButton {...{notificationsPermissionStatus, setNotificationsPermissionStatus}} />}

            {!renderSettings?.disableChatModelsSelect && KB?.kbTitle && !publicChatData && renderChatModelsSelect({
                withoutObsolete: true,
                groupedModels,
                onChange: async (model, vendor) => {
                    setBlockingLoading(true)
                    if (ChatData?.chatId && ChatData?.chatModel) {
                        setChatData(prev => ({...prev, chatModel: model}))
                        await updateChat({
                            chatId: ChatData?.chatId,
                            chatModel: model
                        });
                    } else {
                        const chatVendor = MyServices[model]?.vendor || vendor;
                        setKB({ ...KB, model, chatVendor})
                        setKBData({ ...KBData, model, chatVendor})
                        await updateChatModel(model, chatVendor)
                    }

                    setBlockingLoading(false)
                },
                model: (ChatData?.chatModel || KB?.model),
                details: false,
                style: {
                    width: '200px',
                    position: 'absolute',
                    top: renderSettings?.enableGenerationModelsSelect ? (isMobile ? '40px' : '50px') : (isMobile ? '60px' : '70px'),
                    left: isMobile ? '50%' : (open ? drawerWidth : 0) + 100,
                    transform: 'translateX(-50%)'
                },
                variant: 'standard'
            })}

            {renderSettings?.enableGenerationModelsSelect && KB?.kbTitle && renderImageGenerationModelsSelect({
                groupedModels: groupedImageGenerationModels,
                onChange: async (defaultImageToTextModel) => {
                    setBlockingLoading(true)
                    setKB((prev) => ({ ...prev, variables: {...prev?.variables, defaultImageToTextModel}}))
                    setKBData((prev) => ({ ...prev, variables: {...prev?.variables, defaultImageToTextModel}}))
                    await updateVariable('defaultImageToTextModel', defaultImageToTextModel)
                    setBlockingLoading(false)
                },
                model: (ChatData?.variables?.defaultImageToTextModel
                    || KB?.variables?.defaultImageToTextModel
                    || groupedImageGenerationModels?.[Object.keys(groupedImageGenerationModels)[0]]?.[0]?.id
                ),
                details: false,
                style: {
                    width: '200px',
                    position: 'absolute',
                    top: isMobile ? '70px' : '80px',
                    left: isMobile ? '50%' : (open ? drawerWidth : 0) + 100,
                    transform: 'translateX(-50%)'
                },
                variant: 'standard'
            })}

            <ChatInputProvider>
                <div ref={dynamicContainerRef}>
                    {myKB?.kbId && <DynamicComponentLoader
                        componentName="Header"
                        componentUrl={getContentRenderURL(myKB.kbId)}
                        componentProps={loadedComponentParams}
                    />}
                </div>
                <Box mt={0}
                     className={`chat-container ${wideScroll  ? "chat-container-wide-scrollbar" : ""}`}
                     ref={chatContainerRef}
                     style={chatContainerStyle} >
                    <ChatAvatarBackground kbId={kbId} />
                    {dynamicRenderLoaded && <ChatMessages {...{ KB: myKB, chatId, setWideScroll, setBlockingLoading, onImageToTextResponse, messages,
                        setMessages, setLastEvaluatedKey, lastEvaluatedKey, imageFiles, isStreaming, RequestChatAPI, typing,
                        chatContainerRef, isImageEditor, contextItems, setContextItems, contextWindow,
                        setContextWindow, hasIndexedDBItems, isContextItemsOpen, setIsContextItemsOpen,
                        totalTokensSum, setTotalTokensSum, renderSettings, contentRenderLoaded, blockingLoading, sendButtonRef,
                        sendButtonRippleRef
                    }} KBId={kbId} />}
                </Box>

                <Box mt={2} display="flex"
                     sx={{transition: 'margin-left 0.2s', ...(open && { marginLeft: `${drawerWidth}px`})}}
                     justifyContent="space-between" position="fixed" bottom={0} left={0} right={0}
                     px={0.5} pb={0.5}>
                    {publicChatData ? (
                        <Grid container spacing={1}>
                            <Grid item xs={9}>
                                <ChatInput {...inputComponentParams} />
                            </Grid>
                            <Grid item xs={3}>
                                <ChatButtons {...chatButtonsParams} />
                            </Grid>
                        </Grid>
                    ) : (
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={12} md={9}>
                                <ChatInput {...{...inputComponentParams}} />
                            </Grid>
                            <Grid item xs={12} sm={12} md={3} justifyContent="center" style={{ display: 'flex'}}>
                                <ChatButtons {...chatButtonsParams} />
                            </Grid>
                        </Grid>
                    )}
                </Box>
            </ChatInputProvider>
        </Container>
    );
});